// @ts-nocheck
import { StylesConfig } from 'react-select'
import client from '../api/client'
import { formatPrice } from '../components/CartCheckout/utils'
import { fontFamily } from '../components/common/styled'
import { roundAmount } from './roundAmount'

export const borderStyle = (errors, name: string) => ({
  border: errors[name as keyof typeof errors]
    ? '1px solid red'
    : '1px solid rgba(12, 124, 246, 0.5)',
})

const getStyleFor = (customStyle, name) => {
  return typeof customStyle[name] === 'function' ? customStyle[name]() : {}
}

export const getYearDiff = (d1) => {
  let diff = (d1.getTime() - new Date().getTime()) / 1000
  diff /= 60 * 60 * 24
  return Math.abs(Math.round(diff / 365.25))
}

export const isDOBValid = (val) => {
  const enteredDate = new Date(val)
  return enteredDate.getTime() < new Date().getTime()
}

const getBrowserName = () => {
  if (!navigator || !navigator?.userAgent) return 'unknown'

  let browserInfo = navigator.userAgent

  if (browserInfo.includes('Opera') || browserInfo.includes('Opr'))
    return 'opera'

  if (browserInfo.includes('Edg')) return 'edge'

  if (browserInfo.includes('Chrome')) return 'chrome'

  if (browserInfo.includes('Safari')) return 'safari'

  if (browserInfo.includes('Firefox')) return 'firefox'

  return 'unknown'
}

const autofillcolor = {
  safari: 'transparent',
}

export const dropdownCustomStyles = (
  customControlStyle: object = {},
): StylesConfig => {
  const browser = getBrowserName()

  const controlStyle = getStyleFor(customControlStyle, 'control')
  const singleValueStyle = getStyleFor(customControlStyle, 'singleValue')
  const valueContainerStyle = getStyleFor(customControlStyle, 'valueContainer')
  const inputStyle = getStyleFor(customControlStyle, 'input')
  const menuStyle = getStyleFor(customControlStyle, 'menu')
  const dropdownIndicatorStyle = getStyleFor(
    customControlStyle,
    'dropdownIndicator',
  )

  const placeholderStyle = getStyleFor(customControlStyle, 'placeholder')

  let shouldDisplayMenu = false

  return {
    control: (base, state) => {
      shouldDisplayMenu = state.isFocused

      const border = state.isFocused
        ? {
            border: '1px solid rgba(12, 124, 246, 0.5) !important',
            boxShadow: 'inset 0px 0px 8px rgba(12, 124, 246, 0.25) !important',
          }
        : {
            border: '1px solid rgba(153,159,181,0.5)',
            boxShadow: 'none',
            borderRadius: '4px',
          }
      return {
        ...base,
        outline: 'none',
        fontSize: '14px',
        ...border,
        ...controlStyle,
        fontFamily: fontFamily.REGULAR,
      }
    },
    singleValue: (base: object) => ({
      ...base,
      fontSize: '14px',
      ...singleValueStyle,
      fontFamily: fontFamily.REGULAR,
    }),
    input: (base: object) => ({
      ...base,
      ...inputStyle,
      position: 'relative',
      fontFamily: fontFamily.REGULAR,
      input: {
        [`:-webkit-autofill,
        :-webkit-autofill:hover,
        :-webkit-autofill:focus,
        :-webkit-autofill:active`]: {
          ' -webkit-text-fill-color': '#000',
          ' -webkit-box-shadow': `0 0 0px 1000px ${
            autofillcolor[browser] || 'white'
          } inset`,
          transition: 'background-color 5000s ease-in-out 0s',
        },
      },
    }),
    menu: (base: object) => ({
      ...base,
      fontSize: '14px',
      ...menuStyle,
      fontFamily: fontFamily.REGULAR,
      display: !shouldDisplayMenu && 'none',
      zIndex: 999,
    }),
    dropdownIndicator: (provided: object) => ({
      ...provided,
      svg: {
        margin: '0 5px 0 0 !important',
      },
      ...dropdownIndicatorStyle,
    }),
    valueContainer: (base: object) => ({
      ...base,
      ...valueContainerStyle,
      gridAutoColumns: '1fr',
    }),
    placeholder: (defaultStyles: object) => {
      return {
        ...defaultStyles,
        ...placeholderStyle,
        color: '#99a0b5',
      }
    },
  }
}

export const requiredAddressKeys = (countryISO) => {
  // address2 is optional field to fill up in the checkout form.
  return countryAutoCompleteAddressFormat(countryISO)
    .flat()
    .map((j) => (j === 'zip' ? 'postal_code' : j))
    .filter((i) => i !== 'address2')
}

export const countryAutoCompleteAddressFormat = (iso: string) => {
  switch (iso) {
    case 'JPN': {
      return [['state', 'city'], 'address1', 'address2', 'zip']
    }
    case 'USA': {
      return [
        ['address1', 'address2'],
        ['city', 'state', 'zip'],
      ]
    }
    case 'FRA': {
      return ['address1', 'address2', ['city', 'state'], 'zip']
    }
    case 'POL': {
      return ['address1', 'address2', ['city', 'zip']]
    }
    case 'ESP': {
      return ['address1', 'address2', ['city', 'state'], 'zip']
    }
    case 'RUS': {
      return ['address1', 'address2', ['city', 'state'], 'zip']
    }
    case 'PRT': {
      return ['address1', 'address2', ['city', 'zip']]
    }
    default:
      return [['address1', 'address2'], ['city', 'state'], 'zip']
  }
}

export const prepareAppleShippingOptions = (
  apple_shipping_options = [],
  currency_code,
  selected_shipping_option,
) => {
  const new_shipping_address = apple_shipping_options.map((sh_opt) => {
    const { code, name, price } = sh_opt
    const apple_shipping_opt = {
      id: code,
      label: name,
      amount: {
        currency: currency_code,
        value: Number(price),
      },
    }
    if (code === selected_shipping_option) apple_shipping_opt['selected'] = true
    return apple_shipping_opt
  })
  return new_shipping_address
}

export const prepareApplePaymentDetails = (
  currencyCode,
  cartTotal,
  paymentDetail,
  shippingOptions,
  fullfilment,
) => {
  const dynamicCharge = paymentDetail?.dynamic_charge

  let pay_details = {
    total: {
      label: 'Fluid Express Checkout',
      amount: {
        value: cartTotal,
        currency: currencyCode,
      },
    },
    displayItems: [
      {
        label: 'Item Total',
        amount: {
          value: paymentDetail?.sub_total,
          currency: currencyCode,
        },
      },
      {
        label: 'Tax',
        amount: {
          value: paymentDetail?.tax,
          currency: currencyCode,
        },
      },
      {
        label: 'Shipping',
        amount: {
          value: paymentDetail?.shipping,
          currency: currencyCode,
        },
      },
      {
        label: 'Discount',
        amount: {
          value: paymentDetail?.applied_discounts?.amount || 0.0,
          currency: currencyCode,
        },
      },
    ],
  }
  if (dynamicCharge && Object.entries(dynamicCharge).length) {
    pay_details.displayItems = [
      ...pay_details.displayItems,
      {
        label: 'Handling',
        amount: {
          value:
            roundAmount(
              Object.values(dynamicCharge).reduce(
                (acc, result) => acc + (result ?? 0),
                0,
              ),
            ) || 0,
          currency: currencyCode,
        },
      },
    ]
  }
  if (shippingOptions)
    pay_details['shippingOptions'] = prepareAppleShippingOptions(
      shippingOptions,
      currencyCode,
      fullfilment,
    )
  return pay_details
}

//Dummy Data
export const shippingAddress = {
  addressLine: ['524 S W Temple St B'],
  city: 'Salt Lake City',
  country: 'US',
  dependentLocality: '',
  organization: '',
  phone: '',
  postalCode: '84101',
  recipient: 'Fluid Test',
  region: 'UT',
  sortingCode: '',
}

export const new_apple = {
  transaction: {
    apple_pay: {
      payment_data: {
        data: '32LN+JoImi7gojV2NuIblDKplpODc+yYcWUnqdGnmEMJ+MX86BaP2c03YtR0KQlknT5n3zEFOXKIa616TNI3DVemooch3VwFdsFTfq4CC6cA1mjJv4F/DgmGVW+qHY8FKvauGUnoO2zgv/2dP+QGqVjWlibrAX5VGZ9y6UrM11v5+zdstnmHa1ILNQ2m9MeUzmOF5QVyKqeg8YqDYLRjFL07T4greYnDyC+J2ZoUqvbKoVSsgcute3kzpPlpToC8cYGjuWEFcpwTDznNsU049rYOE0Du6m0ZRzaaYX9GcGXDu9K2CPntpcRGEBB5Zd/wQmNbG3t9kx5ylglDio7ukdGmFkdlvV0KKgkWuNNhkOgD9+1xCaLsT2u7O+TGjFR+415KQii93gm1mg==',
        signature:
          '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',
        header: {
          publicKeyHash: 'OL2dbyp1DGPbOknE+uG2kW/0itzpi5vPgCgErzqUgho=',
          ephemeralPublicKey:
            'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEkl8X+COgykPkIBWuHXNynsjnMKs92hH4R/JjI0h3kyGxuNX9lY8nLKZJZm0kQWpr1NNuSejFwRFqlHKGOyMuyQ==',
          transactionId:
            '6ea95f2f6176cba45423d33040bf1e61747824e23a3e7d547ee3815c5aa34010',
        },
        version: 'EC_v1',
      },
      test_card_number: '4111111111111111',
    },
  },
}

export const prepareApplePayPayload = (token, shipping_address) => {
  const fresh_payload = {
    transaction: {
      apple_pay: {
        payment_data: { ...token },
      },
      payment_method: {
        apple_pay: {
          payment_data: { ...token },
        },
        ...shippingAddress,
      },
      shipping_address: { ...shipping_address },
    },
  }
  return fresh_payload
}

export const prepareApplePayPurchasePayload = (
  shipping_address,
  billing_address,
) => {
  const fresh_payload = {
    transaction: {
      billing_address: { ...billing_address },
      shipping_address: { ...shipping_address },
    },
  }
  return fresh_payload
}

export const prepareApplePayPaymentMethodPayload = (appleResponse) => {
  const { details, payerEmail, payerPhone } = appleResponse
  const { billingContact, shippingContact } = details

  let shipping_address = {
    city: shippingContact?.locality,
    state: shippingContact?.administrativeArea,
    zip: shippingContact?.postalCode,
    country: shippingContact?.countryCode,
    name: `${shippingContact?.givenName || ''} ${shippingContact?.familyName}`,
    email: shippingContact.emailAddress,
  }
  let billing_address = {
    city: billingContact?.locality,
    state: billingContact?.administrativeArea,
    zip: billingContact?.postalCode,
    country: billingContact?.countryCode,
    name: `${billingContact?.givenName || ''} ${billingContact?.familyName}`,
    email: payerEmail,
    phone: payerPhone,
  }

  billingContact?.addressLines?.map((adLi, index) => {
    billing_address[`address${index + 1}`] = adLi
    return null
  })

  let fresh_payload = {
    payment_method: {
      apple_pay: {
        payment_data: { ...details?.token?.paymentData },
      },
      retained: true,
      email: payerEmail,
      phone: payerPhone,
      first_name: billingContact?.givenName,
      last_name: billingContact?.familyName,
      city: billingContact?.locality,
      state: billingContact?.administrativeArea,
      zip: billingContact?.postalCode,
      country: billingContact?.countryCode,
      shipping_city: shippingContact.locality,
      shipping_state: shippingContact?.administrativeArea,
      shipping_zip: shippingContact?.postalCode,
      shipping_country: shippingContact?.countryCode,
    },
  }

  shippingContact?.addressLines?.map((adLi, index) => {
    shipping_address[`address${index + 1}`] = adLi
    fresh_payload.payment_method[`shipping_address${index + 1}`] = adLi
    return null
  })

  billingContact?.addressLines?.map((adLi, index) => {
    fresh_payload.payment_method[`address${index + 1}`] = adLi
    return null
  })

  const requestPayload = {
    transaction: { shipping_address, billing_address },
    payment_method: fresh_payload.payment_method,
  }

  return requestPayload
}

export const generateAndDownloadFile = (data) => {
  // Your JSON data
  const jsonData = data

  // Convert JSON data to a string
  const jsonString = JSON.stringify(jsonData, null, 2)

  // Create a Blob (File-like object) with the JSON string
  const blob = new Blob([jsonString], { type: 'text/plain' })

  // Create a URL from the Blob
  const url = URL.createObjectURL(blob)

  // Create a temporary anchor element
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = 'data.json' // Set the filename for the downloaded file
  anchor.click()

  // Cleanup: revoke the URL object
  URL.revokeObjectURL(url)
}

export const findCommonInArray = (arr) => {
  const flatArr = arr.map((item) => {
    const variant_countries = item?.product?.variants?.find(
      (x) => x.id === item?.variant?.id,
    )?.variant_countries
    return variant_countries
  })
  let ll = []
  flatArr.map((q) => {
    q.map((r) => {
      if (ll.some((e) => e === r.country_iso)) return null
      if (
        flatArr.every((array) =>
          array.some((item) => item?.country_iso === r?.country_iso),
        )
      )
        ll.push(r?.country_iso)
      return null
    })
    return null
  })
  return ll
}

export const preparePaypalPaymentDetails = (
  currencyCode,
  cartTotal,
  paymentDetail,
  shippingOptions,
  fulfillment,
) => {
  const dynamicCharge = paymentDetail?.dynamic_charge
  let purchase_units = [
    {
      amount: {
        currency_code: currencyCode,
        value: roundAmount(cartTotal),
        breakdown: {
          item_total: {
            currency_code: currencyCode,
            value: roundAmount(paymentDetail?.sub_total),
          },
          shipping: {
            currency_code: currencyCode,
            value: roundAmount(paymentDetail?.shipping),
          },
          tax_total: {
            currency_code: currencyCode,
            value: roundAmount(paymentDetail?.tax),
          },
          discount: {
            currency_code: currencyCode,
            value: roundAmount(paymentDetail?.applied_discounts?.amount) || 0,
          },
        },
      },
    },
  ]
  if (shippingOptions && shippingOptions?.length) {
    purchase_units[0].shipping = {
      options: preparePaypalShippingOptions(
        shippingOptions,
        currencyCode,
        fulfillment,
      ),
    }
  }
  if (dynamicCharge && Object.entries(dynamicCharge).length) {
    purchase_units[0].amount.breakdown.handling = {
      currency_code: currencyCode,
      value:
        roundAmount(
          Object.values(dynamicCharge).reduce(
            (acc, result) => acc + (result ?? 0),
            0,
          ),
        ) || 0,
    }
  }
  return purchase_units
}

export const preparePaypalShippingOptions = (
  apple_shipping_options = [],
  currency_code,
  selected_shipping_option,
) => {
  const new_shipping_address = apple_shipping_options.map((sh_opt) => {
    const { code, name, price } = sh_opt
    const paypal_shipping_opt = {
      id: code,
      label: name,
      type: 'SHIPPING',
      amount: {
        currency_code: currency_code,
        value: roundAmount(Number(price)),
      },
    }
    if (code === selected_shipping_option)
      paypal_shipping_opt['selected'] = true
    else paypal_shipping_opt['selected'] = false
    return paypal_shipping_opt
  })
  return new_shipping_address
}

export const getCartItemsLength = (cookieString) => {
  const keyValuePairs = cookieString.split('; ')

  // Initialize a variable to store the cartItemsLength value
  let cartItemsLength = null

  // Loop through the key-value pairs to find the cartItemsLength
  for (const pair of keyValuePairs) {
    const [key, value] = pair.split('=')
    if (key === 'cartItemsLength') {
      cartItemsLength = value
      break // Exit the loop once found
    }
  }
  if (!cartItemsLength) return 0
  return cartItemsLength
}

export const getUpdatedCartItems = (item, updatedItem) => {
  if (Object.entries(updatedItem).length === 0) return []
  return item?.map((it) => {
    const hasUpdatedItem = updatedItem?.find((l) => l.id === it?.id)
    if (hasUpdatedItem) return { ...it, ...hasUpdatedItem }
    return it
  })
}

export const getShippingOption = (option: any, currencySymbol: any) => {
  return {
    ...option,
    label: `${option?.name} - ${currencySymbol}${formatPrice(
      parseFloat(option?.price),
    )}`,
    value: option?.code,
  }
}

export const isNotEmpty = (value) => {
  // Use trim() to remove leading and trailing spaces, then check if the value is not empty
  if (typeof value === 'string') return (value || '').trim() !== ''
  else if (typeof value === 'object')
    return (value?.value || value?.label || value?.show || '').trim() !== ''
  else return null
}

export const sendChatRequest = async (appSetting) => {
  const apiHostUrl = appSetting?.api_url_host
  const response = await client.post(
    `${apiHostUrl}/api/company/chat.json?q=${+new Date()}`,
    appSetting,
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )
  return { response }
}

export const mapCustomMessages = (data) => {
  const errorMessages =
    data?.errors?.map((item) => ({
      message: item.message,
      type: 'error',
    })) || []
  const infoMessages =
    data?.infos?.map((item) => ({
      message: item.message,
      type: 'info',
    })) || []
  return { errorMessages, infoMessages }
}
